'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocalStorageValue } from '@react-hookz/web';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Streamdown } from 'streamdown';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { type ChangelogVersion, compareVersions, fetchChangelog } from '@/lib/changelog';
import { cn } from '@/lib/utils';
export const LAST_SEEN_KEY = 'genesys-editor-whats-new';
const PATCH_GROUPING_START_VERSION = '2.5.0';
const usesGroupedPatches = (version: string) => compareVersions(version, PATCH_GROUPING_START_VERSION) >= 0;
const getMinorGroupKey = (version: string) => version.split('.').slice(0, 2).join('.');
const getVersionAnchorId = (version: string) => `whats-new-v-${version.replace(/[^A-Za-z0-9_-]/g, '-')}`;
const SECTION_BADGE_CLASSES: Record<string, string> = {
  'Breaking Changes': 'border-rose-600 bg-rose-100/80 text-rose-800 dark:bg-rose-900/60 dark:text-rose-100',
  Important: 'border-yellow-500/40 bg-yellow-200/20 text-yellow-900 dark:text-yellow-200',
  'Known Issues': 'border-yellow-500/40 bg-yellow-200/20 text-yellow-900 dark:text-yellow-200',
  Features: 'border-emerald-500/40 bg-emerald-500/15 text-emerald-800 dark:text-emerald-300',
  Updates: 'border-sky-500/40 bg-sky-500/15 text-sky-800 dark:text-sky-300',
  Performance: 'border-sky-500/40 bg-sky-500/15 text-sky-800 dark:text-sky-300',
  Fixes: 'border-indigo-500/40 bg-indigo-500/15 text-indigo-800 dark:text-indigo-300',
  Misc: 'border-neutral-500/80 bg-neutral-400/10 text-neutral-800 dark:text-neutral-200'
};

/** Badge styles for a section title: exact key match, else longest known label contained in the title (case-insensitive). */
function getSectionBadgeClasses(title: string): string | undefined {
  const direct = SECTION_BADGE_CLASSES[title];
  if (direct) return direct;
  const haystack = title.toLowerCase();
  let bestKey: string | null = null;
  for (const key of Object.keys(SECTION_BADGE_CLASSES)) {
    if (!haystack.includes(key.toLowerCase())) continue;
    if (!bestKey || key.length > bestKey.length) bestKey = key;
  }
  return bestKey ? SECTION_BADGE_CLASSES[bestKey] : undefined;
}
interface VersionContentProps {
  version: ChangelogVersion;
  isUnreleased: boolean;
}

/** Renders a single version's changelog content. */
const VersionContent = t0 => {
  const $ = _c(15);
  const {
    version,
    isUnreleased
  } = t0;
  let t1;
  if ($[0] !== version.version) {
    t1 = <h2 className="text-base font-bold text-foreground select-text">{version.version}</h2>;
    $[0] = version.version;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== isUnreleased) {
    t2 = isUnreleased && <span className="text-sm text-foreground select-text font-semibold">Unreleased</span>;
    $[2] = isUnreleased;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== version.date) {
    t3 = version.date && <span className="text-xs text-muted-foreground select-text">{version.date}</span>;
    $[4] = version.date;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <div className="flex items-baseline gap-3">{t1}{t2}{t3}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== version.sections) {
    t5 = version.sections.map(_temp7);
    $[10] = version.sections;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <div className="space-y-4">{t4}{t5}</div>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};
VersionContent.displayName = 'VersionContent';
export interface WhatsNewDialogProps {
  /** 'whatsNew' shows only unseen versions; 'changelog' shows all versions. */
  mode: 'whatsNew' | 'changelog';
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-filtered versions to display (for whatsNew mode). If omitted, all versions are fetched. */
  versions?: ChangelogVersion[];
  /** Current app version to determine unreleased versions and default selection. */
  currentVersion?: string;
}
export const WhatsNewDialog = t0 => {
  const $ = _c(64);
  const {
    mode,
    isOpen,
    onOpenChange,
    versions: versionsProp,
    currentVersion
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      defaultValue: "",
      initializeWithValue: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const lastSeen = useLocalStorageValue(LAST_SEEN_KEY, t1);
  let t2;
  if ($[1] !== versionsProp) {
    t2 = versionsProp ?? [];
    $[1] = versionsProp;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [versions, setVersions] = useState(t2);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [loading, setLoading] = useState(false);
  const hasAppliedInitialScroll = useRef(false);
  let t3;
  if ($[3] !== currentVersion) {
    t3 = version => currentVersion ? compareVersions(version, currentVersion) > 0 : false;
    $[3] = currentVersion;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const isUnreleased = t3;
  const scrollToVersion = _temp8;
  let t4;
  if ($[5] !== currentVersion) {
    t4 = sortedVersions => {
      if (sortedVersions.length === 0) {
        return null;
      }
      if (!currentVersion) {
        return sortedVersions[0]?.version ?? null;
      }
      const releasedVersion = sortedVersions.find(v => compareVersions(v.version, currentVersion) <= 0);
      return releasedVersion?.version ?? sortedVersions[0]?.version ?? null;
    };
    $[5] = currentVersion;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const getDefaultVersion = t4;
  let t5;
  if ($[7] !== getDefaultVersion || $[8] !== isOpen || $[9] !== versionsProp) {
    t5 = () => {
      if (!isOpen) {
        hasAppliedInitialScroll.current = false;
        return;
      }
      if (versionsProp) {
        setVersions(versionsProp);
        setSelectedVersion(getDefaultVersion(versionsProp));
        return;
      }
      setLoading(true);
      fetchChangelog().then(data => {
        const sorted = (data ?? []).sort(_temp9);
        setVersions(sorted);
        setSelectedVersion(getDefaultVersion(sorted));
        setLoading(false);
      });
    };
    $[7] = getDefaultVersion;
    $[8] = isOpen;
    $[9] = versionsProp;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== currentVersion || $[12] !== getDefaultVersion || $[13] !== isOpen || $[14] !== versionsProp) {
    t6 = [isOpen, versionsProp, currentVersion, getDefaultVersion];
    $[11] = currentVersion;
    $[12] = getDefaultVersion;
    $[13] = isOpen;
    $[14] = versionsProp;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  useEffect(t5, t6);
  let t7;
  if ($[16] !== lastSeen || $[17] !== mode || $[18] !== onOpenChange || $[19] !== versions) {
    t7 = open => {
      if (!open && mode === "whatsNew" && versions.length > 0) {
        const latest = versions.reduce(_temp10, versions[0]);
        lastSeen.set(latest.version);
      }
      onOpenChange(open);
    };
    $[16] = lastSeen;
    $[17] = mode;
    $[18] = onOpenChange;
    $[19] = versions;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const handleOpenChange = t7;
  let t8;
  bb0: {
    let t9;
    if ($[21] !== selectedVersion || $[22] !== versions) {
      t9 = versions.find(v_1 => v_1.version === selectedVersion) ?? versions[0];
      $[21] = selectedVersion;
      $[22] = versions;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    const selected = t9;
    if (!selected) {
      let t10;
      if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = [];
        $[24] = t10;
      } else {
        t10 = $[24];
      }
      t8 = t10;
      break bb0;
    }
    if (!usesGroupedPatches(selected.version)) {
      let t10;
      if ($[25] !== selected) {
        t10 = [selected];
        $[25] = selected;
        $[26] = t10;
      } else {
        t10 = $[26];
      }
      t8 = t10;
      break bb0;
    }
    let t10;
    if ($[27] !== selected.version) {
      t10 = getMinorGroupKey(selected.version);
      $[27] = selected.version;
      $[28] = t10;
    } else {
      t10 = $[28];
    }
    const selectedGroup = t10;
    let t11;
    if ($[29] !== selectedGroup || $[30] !== versions) {
      let t12;
      if ($[32] !== selectedGroup) {
        t12 = v_2 => usesGroupedPatches(v_2.version) && getMinorGroupKey(v_2.version) === selectedGroup;
        $[32] = selectedGroup;
        $[33] = t12;
      } else {
        t12 = $[33];
      }
      t11 = versions.filter(t12).sort(_temp11);
      $[29] = selectedGroup;
      $[30] = versions;
      $[31] = t11;
    } else {
      t11 = $[31];
    }
    t8 = t11;
  }
  const visibleVersions = t8;
  let t10;
  let t9;
  if ($[34] !== isOpen || $[35] !== loading || $[36] !== selectedVersion || $[37] !== visibleVersions) {
    t9 = () => {
      if (!isOpen || loading || !selectedVersion || !usesGroupedPatches(selectedVersion)) {
        return;
      }
      if (visibleVersions.length === 0 || hasAppliedInitialScroll.current) {
        return;
      }
      hasAppliedInitialScroll.current = true;
      requestAnimationFrame(() => {
        scrollToVersion(selectedVersion, false);
      });
    };
    t10 = [isOpen, loading, selectedVersion, visibleVersions, scrollToVersion];
    $[34] = isOpen;
    $[35] = loading;
    $[36] = selectedVersion;
    $[37] = visibleVersions;
    $[38] = t10;
    $[39] = t9;
  } else {
    t10 = $[38];
    t9 = $[39];
  }
  useEffect(t9, t10);
  const title = mode === "whatsNew" ? "What's New" : "Changelog";
  const t11 = mode === "whatsNew";
  let t12;
  if ($[40] !== title) {
    t12 = <DialogHeader className="px-4 pt-4 pb-0 shrink-0"><DialogTitle>{title}</DialogTitle></DialogHeader>;
    $[40] = title;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== loading) {
    t13 = loading && <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">Loading...</div>;
    $[42] = loading;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] !== loading || $[45] !== versions.length) {
    t14 = !loading && versions.length === 0 && <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">No changelog entries found.</div>;
    $[44] = loading;
    $[45] = versions.length;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  let t15;
  if ($[47] !== isUnreleased || $[48] !== loading || $[49] !== selectedVersion || $[50] !== versions || $[51] !== visibleVersions) {
    t15 = !loading && versions.length > 0 && <div className="flex flex-1 min-h-0 mt-3"><div className="w-28 shrink-0 border-r border-border"><ScrollArea className="h-full"><div className="py-1 pr-0">{versions.map(v_3 => {
              const unreleased = isUnreleased(v_3.version);
              return <button key={v_3.version} onClick={() => {
                setSelectedVersion(v_3.version);
                requestAnimationFrame(() => {
                  scrollToVersion(v_3.version, true);
                });
              }} className={cn("w-full text-left px-3 py-2 text-xs font-mono font-bold rounded-r-md transition-colors select-none", v_3.version === selectedVersion ? unreleased ? "bg-purple-500/25 text-purple-700 dark:text-purple-400" : "bg-primary text-primary-foreground" : unreleased ? "text-violet-600 dark:text-violet-400/70 hover:text-violet-700 dark:hover:text-violet-300 hover:bg-violet-500/15" : "text-foreground hover:bg-primary/25")}>{v_3.version}</button>;
            })}</div></ScrollArea></div><ScrollArea className="flex-1 min-w-0"><div className="px-5 py-4 select-text cursor-text space-y-8">{visibleVersions.map((version_1, index) => <section key={version_1.version} id={getVersionAnchorId(version_1.version)} className={cn("scroll-mt-3", index < visibleVersions.length - 1 && "border-b border-border pb-7")}><VersionContent version={version_1} isUnreleased={isUnreleased(version_1.version)} /></section>)}</div></ScrollArea></div>;
    $[47] = isUnreleased;
    $[48] = loading;
    $[49] = selectedVersion;
    $[50] = versions;
    $[51] = visibleVersions;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  let t16;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="h-3 shrink-0" />;
    $[53] = t16;
  } else {
    t16 = $[53];
  }
  let t17;
  if ($[54] !== t11 || $[55] !== t12 || $[56] !== t13 || $[57] !== t14 || $[58] !== t15) {
    t17 = <DialogContent aboveLoadingOverlay={t11} className="sm:max-w-3xl h-[700px] max-h-[calc(100vh-4rem)] p-0 flex flex-col gap-0">{t12}{t13}{t14}{t15}{t16}</DialogContent>;
    $[54] = t11;
    $[55] = t12;
    $[56] = t13;
    $[57] = t14;
    $[58] = t15;
    $[59] = t17;
  } else {
    t17 = $[59];
  }
  let t18;
  if ($[60] !== handleOpenChange || $[61] !== isOpen || $[62] !== t17) {
    t18 = <Dialog open={isOpen} onOpenChange={handleOpenChange}>{t17}</Dialog>;
    $[60] = handleOpenChange;
    $[61] = isOpen;
    $[62] = t17;
    $[63] = t18;
  } else {
    t18 = $[63];
  }
  return t18;
};
WhatsNewDialog.displayName = 'WhatsNewDialog';
function _temp(t0) {
  const {
    children
  } = t0;
  return <>{children}</>;
}
function _temp2(t0) {
  const {
    children: children_0
  } = t0;
  return <strong className="select-text">{children_0}</strong>;
}
function _temp3(t0) {
  const {
    className,
    children: children_1,
    ...props
  } = t0;
  const isBlock = typeof className === "string" && className.includes("language-");
  return <code className={cn(className, !isBlock && "rounded bg-muted px-1 py-0.5 font-mono text-xs leading-snug align-baseline select-text")} {...props}>{children_1}</code>;
}
function _temp4(t0) {
  const {
    children: children_2
  } = t0;
  return <ul className="mt-1 ml-3 space-y-0.5">{children_2}</ul>;
}
function _temp5(t0) {
  const {
    children: children_3
  } = t0;
  return <li className="flex gap-2 text-sm text-foreground/90 leading-snug"><span className="mt-1.5 size-1 shrink-0 rounded-full bg-muted-foreground/60" /><span className="select-text">{children_3}</span></li>;
}
function _temp6(item, i) {
  return <li key={i} className="flex gap-2 text-sm text-foreground/90 leading-snug select-text"><span className="mt-1.5 size-1 shrink-0 rounded-full bg-muted-foreground" /><span className="select-text"><Streamdown components={{
        p: _temp,
        strong: _temp2,
        code: _temp3,
        ul: _temp4,
        li: _temp5
      }}>{item}</Streamdown></span></li>;
}
function _temp7(section) {
  return <div key={section.title} className="space-y-1.5"><Badge variant="outline" className={cn("mb-1 select-text", getSectionBadgeClasses(section.title))}>{section.title}</Badge><ul className="space-y-1 pl-1">{section.items.map(_temp6)}</ul></div>;
}
function _temp8(version_0, smooth) {
  if (typeof document === "undefined") {
    return;
  }
  document.getElementById(getVersionAnchorId(version_0))?.scrollIntoView({
    behavior: smooth ? "smooth" : "auto",
    block: "start"
  });
}
function _temp9(a, b) {
  return compareVersions(b.version, a.version);
}
function _temp10(best, v_0) {
  return compareVersions(v_0.version, best.version) > 0 ? v_0 : best;
}
function _temp11(a_0, b_0) {
  return compareVersions(b_0.version, a_0.version);
}